import React, { useState } from 'react';
import Link from 'next/link';
import {
  AppBar,
  alpha,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  Typography,
  Box,
  capitalize,
  Theme,
  MenuProps,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { usePathname } from "next/navigation";

interface NavItem {
  title: string;
  href: string;
  children?: NavItem[];
}

export interface NavItemProps {
  data: NavItem[];
}

const NavItem = (props: NavItemProps) => {
  const { data: navItems } = props;
  const pathname = usePathname();

  // We'll use anchorEls to store which menu (by key) is open (and its anchor element)
  const [anchorEls, setAnchorEls] = useState<Record<string, HTMLElement | null>>({});

  // Toggle menu: if it is open, close it; if closed, open it.
  const handleToggle = (event: React.MouseEvent<HTMLElement>, key: string) => {
    setAnchorEls((prev) => ({
      ...prev,
      [key]: prev[key] ? null : event.currentTarget,
    }));
  };

  const handleClose = (key: string) => {
    setAnchorEls((prev) => ({ ...prev, [key]: null }));
  };

  const isActive = (url: string) => {
    if (!url) return false;
    return pathname.endsWith(url);
  };
  
  const isChildActive = (children: NavItem[]): boolean => {
    return children.some((child) => {
      if (isActive(child.href)) {
        return true; // Child is active
      }
      if (child.children) {
        return isChildActive(child.children); // Check for grandchildren
      }
      return false;
    });
  };

  // Render nested menu items (for dropdown and sub-dropdowns)
  const renderMenuItems = (items: NavItem[], parentKey: string) =>
    items.map((item) => {
      const key = `${parentKey}-${item.title}`;

      if (item.children) {
        const hasActiveChild = item.children && isChildActive(item.children);
        return (
          <MenuItem 
            key={key}
            sx={(theme: Theme) => ({
              padding: '6px 6px 6px 12px',
              background:
                isActive(item.href) || hasActiveChild
                    ? theme.palette.grey["800"]
                    : "transparent",
            })}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0px',

              }}
            >
              <Link href={item.href || '#'} passHref style={{ textDecoration: 'none', }}>
                <Typography
                  component="a"
                  sx={(theme: Theme) => ({
                    color: alpha(theme.palette.common.white, 0.8),
                    textDecoration: "none",
                    padding: '6px 10px',
                  })}
                >
                  {capitalize(item.title)}
                </Typography>
              </Link>
              {/* Display an arrow icon to indicate submenu */}
              <Button 
                onClick={(event) => handleToggle(event, key)} 
                sx={(theme: Theme) => ({
                  color: alpha(theme.palette.common.white, 0.8), 
                  padding: '8px'
                })}
              >
                <NavigateNextIcon style={{fontSize: '20px'}} />
              </Button>
            </Box>
            <Menu
              anchorEl={anchorEls[key]}
              open={Boolean(anchorEls[key])}
              onClose={() => handleClose(key)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              sx={(theme: Theme) => ({
                "& .MuiPaper-root": {
                  background: (theme) => theme.palette.grey["500"], // Set background color using theme
                },
                color: alpha(theme.palette.common.white, 0.8),
                marginTop : '-16px',
                marginLeft: '10px',
              })}
              // slotProps={{
              //   paper: menuPaperProps,
              // }}
            >
              {renderMenuItems(item.children, key)}
            </Menu>
          </MenuItem>
        );
      }

      return (
        <MenuItem
          key={key}
          onClick={() => {
            handleClose(parentKey);
          }}
          sx={(theme: Theme) => ({
            background:
            isActive(item.href)
                ? theme.palette.grey["800"]
                : "transparent",
          })}
        >
          <Link href={item.href || '#'} passHref style={{ textDecoration: 'none', padding: '6px' }}>
            <Typography
              component="a"
              sx={(theme: Theme) => ({
                textDecoration: 'none', 
                color: alpha(theme.palette.common.white, 0.8),
              })}
            >
              {capitalize(item.title)}
            </Typography>
          </Link>
        </MenuItem>
      );
    });

    return (
      <AppBar position="static" color="default" sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
        <Toolbar sx={{ gap: 0, backgroundColor: 'transparent', paddingRight: '0px !important' }}>
          {navItems.map((item, index) => {
            const key = item.title;
            const isNotLastItem = index < navItems.length - 1;

            if (item.children) {
              const hasActiveChild = item.children && isChildActive(item.children);
              return (
                <Box key={key} sx={{ display: 'inline-block' }}>
                  <Button
                    size="small"
                    variant="outlined" 
                    onClick={(e) => handleToggle(e, key)} 
                    endIcon={<ExpandMoreIcon fontSize="medium" />}
                    sx={(theme: Theme) => ({
                      color: alpha(theme.palette.common.white, 0.8),
                      fontSize: "14px !important",
                      height: "32px !important",
                      border: "1px solid " + alpha(theme.palette.common.white, 0.3),
                      "&:hover": {
                        border: "1px solid " + alpha(theme.palette.common.white, 0.8),
                        background: alpha(theme.palette.background.paper, 0.2),
                      },
                      backgroundColor: hasActiveChild
                        ? alpha(theme.palette.background.paper, 0.2)
                        : "transparent",
                      ...(isNotLastItem && {
                        borderRightColor: "transparent",
                      }),
                    })}

                  >
                    {item.title.toUpperCase()}
                  </Button>
                  <Menu
                    anchorEl={anchorEls[key]}
                    open={Boolean(anchorEls[key])}
                    onClose={() => handleClose(key)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    sx={(theme: Theme) => ({
                      "& .MuiPaper-root": {
                        background: (theme) => theme.palette.grey["500"],
                      },
                      color: alpha(theme.palette.common.white, 0.8)
                    })}
                    slotProps={{
                      paper: menuPaperProps,
                    }}
                  >
                    {renderMenuItems(item.children, key)}
                  </Menu>
                </Box>
              );
            }
            return (
              <Box key={key} sx={{ display: 'inline-block' }}>
                <Link href={item.href} passHref>
                  <Button 
                    variant="outlined" 
                    sx={(theme: Theme) => ({
                      color: alpha(theme.palette.common.white, 0.8),
                      fontSize: "14px !important",
                      height: "32px !important",
                      border: "1px solid " + alpha(theme.palette.common.white, 0.3),
                      background:
                      isActive(item.href)
                          ? alpha(theme.palette.background.paper, 0.2)
                          : "transparent",
                      "&:hover": {
                        border: "1px solid " + alpha(theme.palette.common.white, 0.8),
                        background: alpha(theme.palette.background.paper, 0.2),
                      },
                      ...(isNotLastItem && {
                        borderRightColor: "transparent",
                      }),
                    })}
                  >
                    {item.title.toUpperCase()}
                  </Button>
                </Link>
              </Box>
            );
          })}
        </Toolbar>
      </AppBar>
  );
};

export default NavItem;

const menuPaperProps: Required<MenuProps>["slotProps"]["paper"] = {
  elevation: 0,
  sx: {
    borderRadius: 0,
    overflow: "visible",
    background: (theme: Theme) => theme.palette.grey["500"],
    mt: 2,
    "&:before": {
      content: '""',
      display: "block",
      position: "absolute",
      top: 0,
      left: 14,
      width: 10,
      height: 10,
      bgcolor: "inherit",
      transform: "translateY(-50%) rotate(45deg)",
      zIndex: 0,
    },
  },
};

